import React, { Component, Suspense, lazy } from "react";
import { unstable_HistoryRouter as HistoryRouter, Route, Routes } from "react-router-dom";
import { createTheme, ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { createBrowserHistory } from "history";
import "./assets/bootstrap-v4/bootstrap.min.css";
import "./App.css";
import { connect } from "react-redux";
import { withSnackbar } from "utils/withSnackbar";
import { compose } from "redux";
import { auth } from "./services";
import {
  setHostAction,
  setAppTheme,
  setGlobalLoader,
  setAppLogo,
  getDropdownProductsData,
  getDropdownOperatorsData,
  getDropdownDevicesData,
  getDropdownAreasData,
  getDropdownOrderStatusesData,
  getDropdownTransactionStatusData,
  getAlertData,
} from "./redux/actions";
import { domainLevelConfig } from "./ui-config";
import SuperAdminRouter from "./components/SuperAdmin/routes";
import BigLoader from "./components/Common/BigLoader";
import AppRouterHoc from "./components/HOC/AppRouterHoc";
// Not lazy: the native Android WebView shell reads #provisioning's innerHTML
// synchronously via onPageFinished the instant the page load completes, with
// no wait for async chunks. A lazy chunk fetch races that and can lose,
// leaving #provisioning unmounted when the native side reads it.
import Provisioning from "./components/MachineAdmin/Provisioning";
import ThemeManager from "./components/MachineAdmin/ThemeManager";
import SettingManager from "./components/MachineAdmin/SettingManager";
import TroubleshotManager from "./components/MachineAdmin/TroubleshotManager";
import KittingPlan from "./components/MachineAdmin/kittingPlan/components/Plan";
import Inventory from "./components/MachineAdmin/Inventory/Products";
import InventoryList from "./components/MachineAdmin/Inventory/InventoryList";
import TemperatureControl from "./components/MachineAdmin/TemperatureControl";
import RestockKittingPlan from "./components/MachineAdmin/kittingPlan/components/Plan/restockKittingPlan";
import { getToken, onMessage } from "firebase/messaging";
import { messagingPromise } from "./firebase"

const customHistory = createBrowserHistory();

import FourNotFour from "./components/FourNotFour";
const Version = lazy(() => import("./components/Version"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const MyMicrowaveMobile = lazy(() => import("./components/MyMicrowaveMobile"));
const Home = lazy(() => import("./components/Home"));
const Login = lazy(() =>
  import(/* WebpackPrefetch: true */ "./components/Login")
);
const LoginMachineAdmin = lazy(() =>
  import("./components/MachineAdmin/LoginMachineAdmin")
);
const MachineDashboard = lazy(() =>
  import("./components/MachineAdmin/MachineDashboard")
);
const About = lazy(() => import("./components/About"));
const Terms = lazy(() => import("./components/Terms"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const Help = lazy(() => import("./components/HelpCenter"));

const Products = lazy(() =>
  import("./components/TouchlessVendingMachine/Products")
);
const Cart = lazy(() => import("./components/TouchlessVendingMachine/Cart"));
const ConfirmTouchlessCheckout = lazy(() =>
  import("./components/TouchlessVendingMachine/ConfirmTouchlessCheckout")
);
const TouchlessPayment = lazy(() =>
  import("./components/TouchlessVendingMachine/PaymentScreen")
);
const ThankYouScreen = lazy(() =>
  import("./components/TouchlessVendingMachine/ThankYouScreen")
);
const QrPaymentScreen = lazy(() =>
  import("./components/TouchlessVendingMachine/QrPayment")
);
const DispensingScreen = lazy(() =>
  import("./components/TouchlessVendingMachine/QrPayment/DispensingScreen")
);
const PaymentThankyouScreen = lazy(() =>
  import("./components/TouchlessVendingMachine/QrPayment/ThankyouScreen")
);
const ScanQR = lazy(() =>
  import("./components/TouchlessVendingMachine/ScanQR")
);

const CategoryContainer = lazy(() =>
  import(
    "./components/TouchlessVendingMachine/IGT/Categories/CategoryContainer"
  )
);
const ProductContainer = lazy(() =>
  import("./components/TouchlessVendingMachine/IGT/Products/ProductContainer")
);
const CartContainer = lazy(() =>
  import("./components/TouchlessVendingMachine/IGT/Cart/CartContainer")
);
const PaymentContainer = lazy(() =>
  import("./components/TouchlessVendingMachine/IGT/Payment/Payment")
);
const DispatchContainer = lazy(() =>
  import("./components/TouchlessVendingMachine/IGT/ItemDispach/Dispatch")
);
const DispatchContainerQr = lazy(() =>
  import("./components/TouchlessVendingMachine/IGT/ItemDispach/DispatchQr")
);
const PaymentConfirm = lazy(() =>
  import("./components/TouchlessVendingMachine/IGT/Payment/PaymentQr")
);

const ReceiptDisplay = lazy(() => import("./components/Receipt/Receipt.jsx"));

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const serial_no = urlParams.get("serial_no");
const amount = urlParams.get("amount");
const action = urlParams.get("action");
const session_id = urlParams.get("session_id");

class App extends Component {

  requestNotificationPermission = async () => {
    if (typeof Notification === "undefined") {
      console.log("Notification API unsupported on this platform; skipping push registration.");
      return;
    }
    try {
      const messaging = await messagingPromise;
      if (!messaging) {
        console.log("Firebase Messaging unsupported on this platform; skipping push registration.");
        return;
      }

      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        const token = await getToken(messaging, {
          vapidKey: "BBvUEcplE8EMC6wMnFp3gjbSYuUw_7fypcM3ITBXxrmzVV1GO8KSY_AYp9xiuQQsiNb7NhwYBInQnrJOeqEgqU8" 
        });
        
        let userLoginToken = null;
        const currentUserData = localStorage.getItem("current_user");
        if (currentUserData) {
            try {
                const parsedUser = JSON.parse(currentUserData);
                userLoginToken = parsedUser.token || parsedUser.access || parsedUser.access_token;
            } catch (error) {
                console.error("Failed to parse user data from local storage.");
            }
        }

        if (userLoginToken) {
            const response = await fetch(`${import.meta.env.VITE_SERVICE_URL}/api/notifications/register/`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${userLoginToken}`
              },
              body: JSON.stringify({ token: token })
            });

            if (!response.ok && response.status !== 400) {
              // 400 = registration_id already exists on backend, silent no-op
              console.error("Failed to save token to backend.", await response.text());
            }
        }
        

      } else {
        console.log("Notification permission denied by user.");
      }
    } catch (error) {
      console.error("Error generating token:", error);
    }
  };

  // ponytail: v6 AppRoute — no <Route> inside. Callers wrap with <Route element={<AppRoute .../>}/>.
  AppRouteBase = ({ component: Component, layout: Layout, ...rest }) => {
    return (
      <Layout history={customHistory}>
        <Component {...rest} />
      </Layout>
    );
  };
  AppRoute = AppRouterHoc(this.AppRouteBase);
  renderRoutes() {
    const AppRoute = this.AppRoute;
    const currentUser = this.props.currentUser || {};
    const showRouter = ["DA", "OP", "SA", "DE", "FO", "EU", "SU", "EM", "DU"];

    if (showRouter.includes(currentUser.type)) {
      return <SuperAdminRouter currentUser={currentUser} AppRoute={AppRoute} />;
    }

    const path = customHistory.location.pathname;
    const shouldRedirect =
      !["/machineadmin", "/login"].includes(path) &&
      !path.includes("forgot-password") &&
      !path.includes("my-microwave-mobile") &&
      !path.includes("webstore") &&
      !path.includes("receipts") &&
      !path.includes("term") &&
      !path.includes("policy") &&
      !path.includes("version");

    if (shouldRedirect) {
      customHistory.push(`/login?next=${path}`);
      auth.remove_user();
      localStorage.setItem("next", path);
    }
    return null;
  }

  componentDidMount() {
    this.requestNotificationPermission();

    messagingPromise.then((messaging) => {
      if (!messaging) return;

      onMessage(messaging, (payload) => {
        console.log("Foreground message received!", payload);

        if (this.props.enqueueSnackbar) {
            this.props.enqueueSnackbar(
                `${payload.notification.title}: ${payload.notification.body}`,
                { variant: 'info' }
            );
        } else {
            alert(`New Notification: ${payload.notification.title}`);
        }

        this.props.getAlertData();
      });
    });

    this.unlisten = customHistory.listen(() => {
      if (
        !this.props.initialyLoaded &&
        !this.props.globalLoader &&
        ((this.props.currentUser.type !== "EM" &&
          customHistory.location.pathname === "/dashboard") ||
          (this.props.currentUser.type === "EM" &&
            customHistory.location.pathname === "/"))
      ) {
        setTimeout(() => {
          this.props.setDropdownData();
        }, 7000);
      }
    });
    const host = window.location.host;
    const hostConfig =
      domainLevelConfig[host] || domainLevelConfig["default"] || {};
    this.props.setHostAction(host);
    document.title = hostConfig.name;
    const favicon = document.getElementById("favicon");
    if (hostConfig.favicon || hostConfig.logo) {
      favicon.href = hostConfig.favicon ? hostConfig.favicon : hostConfig.logo;
    }

    if (hostConfig.themeColor && !this.props.customTheme) {
      this.props.setAppTheme({
        primaryColor: hostConfig.themeColor,
        secondaryColor: hostConfig.themeColor,
        customTheme: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  render() {
    const muiTheme = createTheme(this.props.theme);
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={muiTheme}>
            <Suspense fallback={<BigLoader overlay={false} />}>
              <HistoryRouter history={customHistory}>
                <div style={{ position: "relative", height: "100%" }}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/version" element={<Version />} />

                    {/* Machine Admin UI Routes */}
                    <Route path="/machineadmin" element={<LoginMachineAdmin />} />
                    <Route path="/machinedashboard" element={<MachineDashboard />} />
                    <Route path="/provisioning" element={<Provisioning />} />
                    <Route path="/apply-theme" element={<ThemeManager />} />
                    <Route path="/setting" element={<SettingManager />} />
                    <Route path="/troubleshot" element={<TroubleshotManager />} />
                    <Route path="/kitting-plan" element={<KittingPlan />} />
                    <Route path="/vm_inventory_list" element={<InventoryList />} />
                    <Route path="/vm_inventory" element={<Inventory />} />
                    <Route path="/temprature_control" element={<TemperatureControl />} />
                    <Route path="/restock-kitting-plan" element={<RestockKittingPlan />} />
                    <Route path="/apply-theme/:deviceId" element={<ThemeManager />} />

                    {/*Touchless Vending Machine Routes*/}
                    <Route path="/webstore/qr" element={<ScanQR />} />
                    <Route path="/webstore/products" element={<Products />} />
                    <Route path="/webstore/cart" element={<Cart />} />
                    <Route path="/webstore/confirm-checkout" element={<ConfirmTouchlessCheckout />} />
                    <Route path="/webstore/final-payment" element={<TouchlessPayment />} />
                    <Route path="/webstore/thank-you" element={<ThankYouScreen />} />
                    <Route path="/webstore/payment" element={<QrPaymentScreen />} />
                    <Route path="/webstore/dispense-products" element={<DispensingScreen />} />
                    <Route path="/webstore/payment/thank-you" element={<PaymentThankyouScreen />} />

                    {/*IGT Touchless Vending Machine Routes*/}
                    <Route path="/webstore/igt/products/category_name" element={<CategoryContainer />} />
                    <Route path="/webstore/igt/products/category_name/*" element={<ProductContainer />} />
                    <Route path="/webstore/igt/cart" element={<CartContainer />} />
                    <Route path="/webstore/igt/payment" element={<PaymentContainer />} />
                    <Route path="/webstore/igt/item-dispatch" element={<DispatchContainer />} />
                    <Route path="/webstore/igt/item-dispatch-qr" element={<DispatchContainerQr />} />
                    <Route
                      path="/webstore/igt/products/payments:serial_no?/:amount?/:action?/:session_id?"
                      element={session_id && serial_no && amount && action ? <PaymentConfirm /> : <FourNotFour />}
                    />

                    {/*E Receipt route*/}
                    <Route path="/receipts/:token" element={<ReceiptDisplay />} />

                    <Route path="/forgot-password/:token" element={<ForgotPassword />} />
                    <Route path="/my-microwave-mobile/:deviceId" element={<MyMicrowaveMobile />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/term" element={<Terms />} />
                    <Route path="/policy" element={<PrivacyPolicy />} />
                    <Route path="/help" element={<Help />} />

                    <Route path="/*" element={this.renderRoutes() || (auth.is_loggedin() ? <FourNotFour /> : null)} />
                  </Routes>
                </div>
              </HistoryRouter>
            </Suspense>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.userReducer.current_user,
    theme: state.userReducer.theme,
    customTheme: state.userReducer.customTheme,
    globalLoader: state.userReducer.globalLoader,
    initialyLoaded: state.userReducer.initialyLoaded,
  };
};

const mapDispatchToProps = {
  setHostAction,
  setAppTheme,
  getAlertData,
  setDropdownData: () => (dispatch) => {
    dispatch(getDropdownDevicesData());
    dispatch(getDropdownProductsData());
    dispatch(getDropdownOperatorsData());
    dispatch(getDropdownAreasData());
    dispatch(getDropdownOrderStatusesData());
    dispatch(getDropdownTransactionStatusData());
  },
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withSnackbar
)(App);
